import type { RedoCustomerWidgetRpcClient } from "@redotech/chat-widget-sdk/customer-widget-rpc/client";
import { ConversationListItem } from "@redotech/chat-widget-sdk/customer-widget-rpc/schema/sales-agent/get-conversations-list-items";
import { useRequiredContext } from "@redotech/react-util/context";
import {
  sendMessageCustomerV2,
  updateCustomerCart,
} from "@redotech/redo-api-client/conversations";
import {
  getDefaultQuickResponses,
  GetResponseOutput,
  MessagingConfig,
  QuickResponse,
} from "@redotech/redo-model/concierge-conversation/rpc/concierge-conversation-schema";
import {
  ConversationMessageType,
  ConversationPlatform,
} from "@redotech/redo-model/conversation";
import { ConversationMessage } from "@redotech/redo-model/conversation/conversation-message-db-parser";
import {
  Conversation,
  ConversationSchemaDbParser,
} from "@redotech/redo-model/conversation/conversation-schema-db-parser";
import { parseItem } from "@redotech/redo-model/parse-item";
import { ExtensionShopifyClientContext } from "@redotech/redo-shopify-extension/shopify";
import { useCart } from "@redotech/redo-shopify-extension/utils/cart";
import {
  addConciergeAttributionCartAttribute,
  addConciergeConversationIdToStorage,
} from "@redotech/redo-shopify-extension/utils/concierge-attribution";
import { RedoClientContext } from "@redotech/redo-web/client";
import { AllCookies, getCookie } from "@redotech/redo-web/utils/cookies";
import { ExtensionShopifyClient } from "@redotech/shopify-client/ajax";
import {
  useMutation,
  UseMutationResult,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useContext } from "react";
import { useLocalStorage } from "usehooks-ts";
import { ConversationType } from "../contexts/active-conversation";
import {
  AuthContext,
  authTokenToAuthHeader,
  ConversationAuthContext,
  SetAuthContext,
} from "../contexts/auth";
import { CurrentProductInfoQueryContext } from "../contexts/current-product-info";
import { GetActiveCartIdContext } from "../contexts/get-active-cart-id";
import { RedoCustomerWidgetRpcClientContext } from "../contexts/redo-customer-widget-rpc-client";
import { TeamInfoContext } from "../contexts/team-info";
import {
  createTempConversation,
  getConversationType,
  getNewConversationType,
  isTempConversation,
  optimisticallyAddCustomerMessage,
  TEMP_CONVERSATION_ID,
} from "../utils/add-message-utils";
import { getAnonymousConversationIds } from "../utils/anonymous-conversations-storage";
import { ExpandedInfoToCollect } from "../utils/chat-flows-utils";

// Private utility function for tracking concierge interactions
const trackConciergeInteracted = async (
  rpcClient: RedoCustomerWidgetRpcClient,
  extensionShopifyClient: ExtensionShopifyClient,
  conversationId: string | null,
) => {
  const shopifySessionId = getCookie(AllCookies.ShopifySessionId);

  if (shopifySessionId === undefined) {
    throw new Error("Shopify session id is missing");
  }

  if (conversationId && conversationId !== TEMP_CONVERSATION_ID) {
    addConciergeConversationIdToStorage(conversationId);

    await Promise.all([
      rpcClient.conciergeInteracted({ shopifySessionId, conversationId }),
      addConciergeAttributionCartAttribute(extensionShopifyClient),
    ]);
  }
};

// Re-export for backward compatibility

export interface ConversationListItemWithType extends ConversationListItem {
  conversationType: ConversationType;
}

export interface GetResponseOutputWithMessagesToReplace
  extends GetResponseOutput {
  messagesToReplace?: ConversationMessage[];
  config?: MessagingConfig;
}

export interface ConversationQuickResponses {
  [conversationId: string]: QuickResponse[];
}

// Helper function to map ExpandedInfoToCollect to API parameters
function getCollectedFieldParams(
  collectedInfoType?: ExpandedInfoToCollect,
  conversation?: Conversation,
) {
  const params = {
    emailCollected: false,
    fourDigitCodeId: undefined as string | undefined,
  };

  if (!collectedInfoType) {
    return params;
  }

  switch (collectedInfoType) {
    case ExpandedInfoToCollect.EMAIL:
      params.emailCollected = true;
      break;
    case ExpandedInfoToCollect.CODE_VERIFICATION:
      params.fourDigitCodeId = conversation?.fourDigitCodeId || undefined;
      break;
    // NAME and CUSTOM don't need special API parameters
  }

  return params;
}

export function useAddMessageMutation(): UseMutationResult<
  {
    result: GetResponseOutputWithMessagesToReplace;
    conversationId: string | undefined;
    message?: string;
    currentConversation: Conversation | undefined;
    shouldRefresh?: boolean;
    customerAuthToken?: string;
    conversationAuthToken?: string;
  },
  Error,
  {
    message?: string;
    conversationId?: string;
    handler?: () => Promise<{
      conversationId?: string;
      messages: ConversationMessage[];
      messagesToReplace?: ConversationMessage[];
    }>;
    shouldRefresh?: boolean;
    currentStepId?: number | null;
    collectedInfoType?: ExpandedInfoToCollect;
    isPdpSource?: boolean;
  },
  {
    previousConversation: Conversation | undefined;
    conversationId: string | undefined;
  }
> {
  const queryClient = useQueryClient();
  const rpcClient = useRequiredContext(RedoCustomerWidgetRpcClientContext);
  const client = useRequiredContext(RedoClientContext);
  const auth = useContext(AuthContext);
  const { data: currentProductInfo } = useRequiredContext(
    CurrentProductInfoQueryContext,
  );
  const getActiveCartId = useRequiredContext(GetActiveCartIdContext);
  const extensionShopifyClient = useRequiredContext(
    ExtensionShopifyClientContext,
  );
  const teamInfo = useRequiredContext(TeamInfoContext);
  const [_, setLocalStorageConversationId] = useCurrentConversationId();
  const cart = useCart();
  const setAuth_ = useContext(SetAuthContext);
  const { setConversationAuth, getConversationAuth } = useRequiredContext(
    ConversationAuthContext,
  );
  const { setQuickResponsesForConversation } = useQuickResponses();

  return useMutation({
    mutationKey: ["addMessage"],
    mutationFn: async ({
      message,
      conversationId,
      handler,
      shouldRefresh = false,
      currentStepId,
      collectedInfoType,
      isPdpSource = false,
    }: {
      message?: string;
      conversationId?: string;
      handler?: () => Promise<{
        conversationId?: string;
        messages: ConversationMessage[];
        messagesToReplace?: ConversationMessage[];
      }>;
      shouldRefresh?: boolean;
      currentStepId?: number | null;
      collectedInfoType?: ExpandedInfoToCollect;
      isPdpSource?: boolean;
    }) => {
      const currentConversation = conversationId
        ? queryClient.getQueryData<Conversation>([
            "conversation",
            conversationId,
          ])
        : undefined;

      let result: GetResponseOutputWithMessagesToReplace;
      let customerAuthToken: string | undefined;
      let conversationAuthToken: string | undefined;

      if (handler) {
        const handlerResult = await handler();

        // If handler returned a new conversationId (e.g., temp -> real), use it
        if (handlerResult.conversationId) {
          conversationId = handlerResult.conversationId;
        }

        result = {
          messages: handlerResult.messages,
          messagesToReplace: handlerResult.messagesToReplace,
          newConversationBeforeResponse: null,
        };
      } else {
        // Determine if this is an AI conversation or human conversation
        if (!message) {
          throw new Error("Message is required when no handler is provided");
        }
        const conversationType = getNewConversationType(
          teamInfo.conciergeLiveChat,
          currentConversation,
        );

        let initialMessages =
          currentConversation?._id === TEMP_CONVERSATION_ID
            ? currentConversation?.messages?.map((msg) => ({
                type: msg.type,
                content: msg.content,
              }))
            : undefined;
        // Because we optimistically update the conversation,
        // the last message may be the customer's own placeholder message.
        // We're about to send that message, so we need to remove it from the initial messages
        if (
          initialMessages?.at(-1)?.type === ConversationMessageType.CUSTOMER
        ) {
          initialMessages = initialMessages.slice(0, -1);
        }
        if (conversationType === ConversationType.AI) {
          result = await rpcClient.getConciergeResponse(
            {
              conversationId:
                currentConversation && !isTempConversation(currentConversation)
                  ? currentConversation._id
                  : undefined,
              message,
              customerTimezone:
                Intl.DateTimeFormat().resolvedOptions().timeZone,
              currentProductInfo,
              activeCartId: await getActiveCartId(),
              initialMessages,
              isPdpSource,
              generateQuickResponses: true,
            },
            {
              requestHeaders: auth?.token
                ? authTokenToAuthHeader(auth.token)
                : undefined,
            },
          );

          conversationAuthToken = result.config?.token;
        } else {
          const collectedFieldParams = getCollectedFieldParams(
            collectedInfoType,
            currentConversation,
          );

          const conversationSpecificToken =
            currentConversation &&
            currentConversation._id !== TEMP_CONVERSATION_ID
              ? getConversationAuth(currentConversation._id)
              : undefined;

          const sendMessageResult = await sendMessageCustomerV2(client, {
            conversationId:
              currentConversation &&
              currentConversation._id !== TEMP_CONVERSATION_ID
                ? currentConversation._id
                : undefined,
            token: conversationSpecificToken || auth?.token,
            teamId: teamInfo.id,
            message,
            welcomeMessages: initialMessages,
            currentStepId: currentStepId ?? null,
            emailCollected: collectedFieldParams.emailCollected,
            fourDigitCodeId: collectedFieldParams.fourDigitCodeId,
            isConvertedConversation: conversationType === ConversationType.LIVE,
          });

          if (
            cart?.token &&
            sendMessageResult.conversation?._id &&
            sendMessageResult.config.token
          ) {
            await updateCustomerCart(client, {
              conversationId: sendMessageResult.conversation?._id,
              cartToken: cart.token,
              token:
                conversationSpecificToken ||
                auth?.token ||
                sendMessageResult.config.token,
            });
          }

          customerAuthToken = sendMessageResult.config?.customerAuthToken;
          conversationAuthToken = sendMessageResult.config?.token;

          result = sendMessageResult;
        }
      }

      return {
        result,
        conversationId,
        message,
        currentConversation,
        shouldRefresh,
        customerAuthToken,
        conversationAuthToken,
      };
    },
    onMutate: async ({ message, conversationId }) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: ["conversation", conversationId],
      });

      // Snapshot the previous value
      const previousConversation = queryClient.getQueryData<Conversation>([
        "conversation",
        conversationId,
      ]);
      const conversationType = getNewConversationType(
        teamInfo.conciergeLiveChat,
        previousConversation,
      );

      if (message) {
        const optimisticConversation = optimisticallyAddCustomerMessage(
          message,
          previousConversation,
          conversationType,
          teamInfo,
        );
        queryClient.setQueryData(
          ["conversation", optimisticConversation._id],
          optimisticConversation,
        );
        if (previousConversation?._id !== optimisticConversation._id) {
          setLocalStorageConversationId(optimisticConversation._id);
        }
      }

      // Return a context object with the snapshotted value
      return { previousConversation, conversationId };
    },
    onError: (err, variables, context) => {
      if (context?.conversationId && context?.previousConversation) {
        queryClient.setQueryData(
          ["conversation", context.conversationId],
          context.previousConversation,
        );
      }
    },
    onSuccess: (
      { result, conversationId, customerAuthToken, conversationAuthToken },
      variables,
    ) => {
      let finalConversationId = conversationId;

      // if the conversation is returned use that else just set new messages
      if (result.newConversationBeforeResponse) {
        finalConversationId = result.newConversationBeforeResponse._id;

        queryClient.setQueryData(
          ["conversation", finalConversationId],
          result.newConversationBeforeResponse,
        );
      } else if (
        result.messages.length > 0 ||
        result.messagesToReplace?.length
      ) {
        queryClient.setQueryData(
          ["conversation", finalConversationId],
          (old: Conversation | undefined) => {
            if (!old) return old;

            let updatedMessages = [...old.messages];

            if (result.messagesToReplace?.length) {
              const replaceMessageIds = new Set(
                result.messagesToReplace.map((msg) => msg._id),
              );

              updatedMessages = updatedMessages.map((msg) =>
                replaceMessageIds.has(msg._id)
                  ? result.messagesToReplace!.find(
                      (replaceMsg) => replaceMsg._id === msg._id,
                    ) || msg
                  : msg,
              );
            }

            if (result.messages.length > 0) {
              updatedMessages = [...updatedMessages, ...result.messages];
            }

            return { ...old, messages: updatedMessages };
          },
        );
      }
      if (finalConversationId) {
        setLocalStorageConversationId(finalConversationId);
      }

      // Handle token setting in onSuccess
      if (customerAuthToken && auth?.token !== customerAuthToken) {
        setAuth_?.(customerAuthToken);
      }

      if (conversationAuthToken && finalConversationId) {
        setConversationAuth(finalConversationId, conversationAuthToken);
      }

      // Store quickResponses if they exist and we have a conversation ID
      if (result.quickResponses && finalConversationId) {
        setQuickResponsesForConversation(
          finalConversationId,
          result.quickResponses,
        );
      }

      // Track concierge interaction when we have a final conversation ID
      if (finalConversationId) {
        void trackConciergeInteracted(
          rpcClient,
          extensionShopifyClient,
          finalConversationId,
        ).catch((error) => {
          console.error("Failed to track concierge interaction:", error);
        });
      }

      // Invalidate conversations list to ensure it's refreshed
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    onSettled: (data, error, variables) => {
      if (variables.shouldRefresh && variables.conversationId) {
        void queryClient.invalidateQueries({
          queryKey: ["conversation", variables.conversationId],
        });
      }
    },
  });
}

export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useCallback(
    (conversation: Conversation) => {
      queryClient.setQueryData(
        ["conversation", conversation._id],
        conversation,
      );
      void queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
    [queryClient],
  );
}

export function useConversation(conversationId: string): {
  conversation: Conversation | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const rpcClient = useRequiredContext(RedoCustomerWidgetRpcClientContext);
  const teamInfo = useRequiredContext(TeamInfoContext);

  const {
    data: conversation,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["conversation", conversationId],
    queryFn: async (): Promise<Conversation | undefined> => {
      if (!conversationId || conversationId === TEMP_CONVERSATION_ID) {
        // Check if there's already a temp conversation in the cache (for pre-scripted conversations)
        const existingTempConversation = queryClient.getQueryData<Conversation>(
          ["conversation", TEMP_CONVERSATION_ID],
        );

        if (existingTempConversation) {
          return existingTempConversation;
        }

        // Since we are using undefined for the conversation it will just return the default type for out settings
        const conversationType = getNewConversationType(
          teamInfo.conciergeLiveChat,
          undefined,
        );
        return createTempConversation([], conversationType, teamInfo);
      }

      const response = await rpcClient.getConversationById({
        conversationId: conversationId.toString(),
      });
      return parseItem(
        response.conversation,
        ConversationSchemaDbParser,
        "Error parsing conversation",
        conversationId,
      );
    },
    enabled: !!conversationId,
    staleTime: 1000 * 60 * 5, // 5 minutes
    gcTime: 1000 * 60 * 10, // 10 minutes
  });

  return { conversation, isLoading, error: error as Error | null };
}

export const useConversationItems = (): {
  conversationItems: ConversationListItemWithType[] | undefined;
  isPending: boolean;
  error: Error | null;
  refetch: () => void;
} => {
  const teamInfo = useRequiredContext(TeamInfoContext);
  const auth = useContext(AuthContext);
  const rpcClient = useRequiredContext(RedoCustomerWidgetRpcClientContext);

  const {
    data: conversationItems,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["conversations", teamInfo.id],
    queryFn: async (): Promise<ConversationListItemWithType[]> => {
      let items: ConversationListItem[] = [];
      if (!auth?.token) {
        const conversationIds = getAnonymousConversationIds();

        const rpcResult = await rpcClient.getConversationsListItems({
          conversationIds,
        });
        items = items.concat(rpcResult.listItems);
      } else {
        const rpcResult = await rpcClient.getConversationsListItems(
          {},
          { requestHeaders: authTokenToAuthHeader(auth.token) },
        );
        items = items.concat(rpcResult.listItems);
      }
      return items
        .map((item) => ({
          ...item,
          conversationType: getConversationType(
            (item.platform ??
              ConversationPlatform.REDO_CHAT) as ConversationPlatform,
            item.assignee,
          ),
        }))
        .sort(
          (a, b) =>
            new Date(b.lastMessageCreatedAt).getTime() -
            new Date(a.lastMessageCreatedAt).getTime(),
        );
    },
    enabled: !!teamInfo.id,
    staleTime: 1000 * 60 * 2, // 2 minutes
    gcTime: 1000 * 60 * 5, // 5 minutes
  });

  return { conversationItems, isPending, error, refetch };
};

export const REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY = "redoChatWidget";
export const useCurrentConversationId = () => {
  return useLocalStorage<string>(
    REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY + "_currentConversationId",
    TEMP_CONVERSATION_ID,
  );
};

export const usePreviousConversationId = () => {
  return useLocalStorage<string | undefined>(
    REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY + "_previousConversationId",
    undefined,
  );
};

export const useQuickResponses = () => {
  const [quickResponses, setQuickResponses] =
    useLocalStorage<ConversationQuickResponses>(
      REDO_CHAT_WIDGET_LOCAL_STORAGE_KEY + "_quickResponses",
      {},
    );

  const setQuickResponsesForConversation = useCallback(
    (conversationId: string, responses: QuickResponse[]) => {
      setQuickResponses((prev) => ({ ...prev, [conversationId]: responses }));
    },
    [setQuickResponses],
  );

  const teamInfo = useRequiredContext(TeamInfoContext);

  const getQuickResponsesForConversation = useCallback(
    (conversationId: string): QuickResponse[] => {
      if (conversationId === TEMP_CONVERSATION_ID) {
        // Check if concierge is enabled and using AI mode
        if (
          !teamInfo ||
          !teamInfo.conciergeEnabled ||
          !teamInfo.conciergeLiveChat
        ) {
          return [];
        }

        const skills = teamInfo.conciergeSettings?.skills;
        if (!skills) {
          return [];
        }

        return getDefaultQuickResponses(skills);
      }
      return quickResponses[conversationId] || [];
    },
    [quickResponses, teamInfo],
  );

  const clearQuickResponsesForConversation = useCallback(
    (conversationId: string) => {
      setQuickResponses((prev) => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
    },
    [setQuickResponses],
  );

  return {
    setQuickResponsesForConversation,
    getQuickResponsesForConversation,
    clearQuickResponsesForConversation,
  };
};

export { type AnonymousConversation } from "../utils/anonymous-conversations-storage";
